import { motion } from "framer-motion";
import { Heart, Zap, Users, BookOpen, TrendingUp, Server } from "lucide-react";
import StudyMateLogo from "../components/StudyMateLogo";

export default function Donations() {
  const benefits = [
    {
      icon: <Server size={32} className="text-cyan-400" />,
      title: "Server Costs",
      description: "Keep the platform running 24/7 with fast, reliable servers"
    },
    {
      icon: <Users size={32} className="text-purple-400" />,
      title: "Multiplayer Improvements",
      description: "Enhanced matchmaking, lobby systems, and team features"
    },
    {
      icon: <BookOpen size={32} className="text-teal-400" />,
      title: "New Subjects & Topics",
      description: "Expand question pools with more subjects and difficulty levels"
    },
    {
      icon: <TrendingUp size={32} className="text-blue-400" />,
      title: "Ranked Features",
      description: "Advanced ELO systems, seasons, tournaments, and leaderboards"
    },
    {
      icon: <Zap size={32} className="text-amber-400" />,
      title: "Future Updates",
      description: "New game modes, AI features, achievements, and cosmetics"
    }
  ];

  const handleDonate = () => {
    window.open("https://ko-fi.com/studymateranked", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen pb-20 px-4">
      <div className="absolute inset-0 grid-bg opacity-30" />
      
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative max-w-4xl mx-auto pt-12 pb-8 text-center donate-hero"
      >
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 animate-pulse blur-xl bg-gradient-radial from-cyan-500/30 to-transparent rounded-full" />
            <Heart size={64} className="relative text-cyan-400 animate-pulse" />
          </div>
        </div>

        <h1 className="font-heading font-black text-3xl sm:text-5xl md:text-6xl mb-4 px-2 leading-tight bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
          <span className="block sm:inline">SUPPORT </span>
          <span className="block sm:inline">STUDYMATE</span>
        </h1>
        
        <div className="font-mono text-xs tracking-[0.4em] text-cyan-400 uppercase mb-6">
          RANKED
        </div>

        <p className="text-lg text-zinc-300 max-w-2xl mx-auto leading-relaxed mb-8">
          Help us build the ultimate competitive study platform. Your support keeps the servers running, 
          enables new features, and makes StudyMateRanked better for everyone.
        </p>

        {/* Main Ko-fi Button */}
        <motion.button
          onClick={handleDonate}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="group relative px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-xl font-mono uppercase tracking-[0.2em] text-sm font-bold text-white shadow-2xl shadow-cyan-500/50 overflow-hidden"
          data-testid="donate-main-button"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <div className="relative flex items-center gap-3">
            <Heart size={20} className="group-hover:animate-pulse" />
            <span>Support on Ko-fi</span>
          </div>
        </motion.button>

        <p className="text-xs text-zinc-500 mt-4">
          Securely donate via Ko-fi • No account required • One-time or monthly
        </p>
      </motion.div>

      {/* What Your Support Enables */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="relative max-w-6xl mx-auto mt-16"
      >
        <h2 className="text-3xl font-heading font-black text-center mb-12 text-white">
          What Your Support Enables
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, idx) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + idx * 0.1 }}
              className="ba-card p-6 group hover:border-cyan-500/50 transition-all duration-300"
            >
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {benefit.icon}
                </div>
                <h3 className="font-mono text-sm tracking-wider uppercase text-white mb-2 font-bold">
                  {benefit.title}
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Ko-fi Widget Embed (Optional) */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="relative max-w-2xl mx-auto mt-16 ba-card p-8 text-center"
      >
        <h3 className="text-2xl font-heading font-black text-white mb-4">
          Every Contribution Helps
        </h3>
        <p className="text-zinc-400 mb-6">
          Whether it's a coffee or a monthly pledge, your support directly impacts the platform's growth and sustainability.
        </p>
        
        {/* Ko-fi Button Widget */}
        <div className="flex justify-center">
          <a
            href="https://ko-fi.com/studymateranked"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 rounded-lg font-semibold text-white transition-all duration-300 shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 hover:scale-105"
            data-testid="donate-kofi-widget"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.022 11.822c.164 2.424 2.586 2.672 2.586 2.672s8.267-.023 11.966-.049c2.438-.426 2.683-2.566 2.658-3.734 4.352.24 7.422-2.831 6.649-6.916zm-11.062 3.511c-1.246 1.453-4.011 3.976-4.011 3.976s-.121.119-.31.023c-.076-.057-.108-.09-.108-.09-.443-.441-3.368-3.049-4.034-3.954-.709-.965-1.041-2.7-.091-3.71.951-1.01 3.005-1.086 4.363.407 0 0 1.565-1.782 3.468-.963 1.904.82 1.832 3.011.723 4.311zm6.173.478c-.928.116-1.682.028-1.682.028V7.284h1.77s1.971.551 1.971 2.638c0 1.913-.985 2.667-2.059 3.015z"/>
            </svg>
            <span>Support me on Ko-fi</span>
          </a>
        </div>
      </motion.div>

      {/* Thank You Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="relative max-w-4xl mx-auto mt-16 text-center"
      >
        <div className="flex flex-col sm:inline-flex sm:flex-row items-center justify-center gap-3 sm:gap-3 px-5 py-4 sm:px-6 sm:py-3 border border-cyan-500/30 rounded-2xl sm:rounded-full bg-cyan-500/5 max-w-xs sm:max-w-none mx-auto">
          <StudyMateLogo compact iconOnlyMobile />
          <span className="text-cyan-400 font-mono text-xs sm:text-sm tracking-wider text-center">Thank you for your support!</span>
        </div>
      </motion.div>
    </div>
  );
}
